import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, 
  Eye, 
  Mail, 
  Phone,
  MessageCircle,
  TrendingUp,
  Calendar,
  Search
} from 'lucide-react';
import { usePermissions } from '@hooks/usePermissions';
import { ADMIN_PERMISSIONS } from '@constants/admin.constants';
import apiService from '@services/api/api.service';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import StatsCard from '@components/admin/StatsCard';
import FilterBar from '@components/admin/FilterBar';
import Pagination from '@components/admin/Pagination';
import PermissionGuard from '@components/admin/PermissionGuard';
import toast from 'react-hot-toast';

const StudentList = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    page: 1,
    limit: 10
  });

  const statusFilters = [
    { value: 'all', label: 'All Students' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  useEffect(() => {
    if (!hasPermission(ADMIN_PERMISSIONS.VIEW_STUDENTS)) {
      toast.error('Access denied');
      navigate('/admin/dashboard');
      return;
    }
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/admin/students', { params: filters });
      
      if (response.success) {
        setStudents(Array.isArray(response?.data?.data) ? response?.data?.data : []);
        setPagination(response?.data?.pagination || {});
        setStats(response?.data?.stats || {});
      }
    } catch (error) {
      console.error('Failed to load student data:', error);
      toast.error('Failed to load student data');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleViewDetails = (studentId) => {
    navigate(`/admin/students/${studentId}`);
  };

  return (
    <PermissionGuard requiredPermission={ADMIN_PERMISSIONS.VIEW_STUDENTS}>
      <div className="min-h-full bg-gray-50 p-3">
        {/* <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary-900 mb-2">
            Student Management
          </h1>
          <p className="text-primary-600">
            View and manage all students
          </p>
        </div> */}

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <StatsCard
              title="Total Students"
              value={stats.totalStudents || 0}
              icon={GraduationCap}
              color="primary"
            />
            <StatsCard
              title="Active Students"
              value={stats.activeStudents || 0}
              icon={TrendingUp}
              color="green"
            />
            <StatsCard
              title="Total Questions"
              value={stats.totalQuestions || 0}
              icon={MessageCircle}
              color="blue"
            />
            <StatsCard
              title="This Month"
              value={stats.newThisMonth || 0}
              icon={Calendar}
              color="purple"
            />
          </div>
        )}

        <Card className="mb-3 p-4">
          <FilterBar
            searchValue={filters.search}
            onSearchChange={(value) => handleFilterChange('search', value)}
            filters={statusFilters}
            activeFilter={filters.status}
            onFilterChange={(value) => handleFilterChange('status', value)}
            placeholder="Search by name, email, or phone..."
          />
        </Card>

        <Card className="overflow-hidden">
          <div className="p-3 border-b border-gray-200">
            <h2 className="text-base font-semibold text-primary-900">
              All Students
            </h2>
          </div>

          {loading ? (
            <div className="p-8 flex justify-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : students.length === 0 ? (
            <div className="p-8 text-center">
              <GraduationCap className="w-16 h-16 text-primary-300 mx-auto mb-4" />
              <p className="text-primary-600">No students found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Questions Asked
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Active
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {students.map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center">
                            {student.profilePicture ? (
                              <img
                                src={`${student.profilePicture}`}
                                alt={`${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                                {student.firstName?.[0]?.toUpperCase() || student.lastName?.[0]?.toUpperCase() || student.email?.[0]?.toUpperCase()}
                              </div>
                            )}
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {`${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2 text-sm text-gray-900">
                              <Mail className="w-4 h-4 text-gray-500" />
                              {student.email}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Phone className="w-4 h-4 text-gray-500" />
                              {student.phone || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <MessageCircle className="w-4 h-4 text-blue-500" />
                            <span className="text-sm font-semibold text-gray-900">
                              {student.questionCount || 0}
                            </span>
                            <span className="text-xs text-gray-500">questions</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                          {student.lastActiveAt 
                            ? new Date(student.lastActiveAt).toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                          {new Date(student.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(student.id)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination && (
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.total}
                  itemsPerPage={pagination.limit}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </Card>
      </div>
    </PermissionGuard>
  );
};

export default StudentList;
